import { authFetch } from './auth-fetch';
import type { Page } from './endpoints';
import type { AccessLevel, ProjectMember, ProjectRole } from './projects';

/**
 * The organization and employee half of the API (`12 §4` and `§5`).
 *
 * Split from `endpoints.ts` because phase 2 roughly doubles the surface and one
 * file listing forty calls stops being the thing that made the original worth
 * having — a place where you can see, at a glance, everything this frontend asks
 * the server for. Same rule as before: no client-side modelling and no renaming.
 */

export interface Department {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  mission: string | null;
  parent_department_id: string | null;
  employee_count: number;
  archived_at: string | null;
  created_at: string;
}

export interface DepartmentNode extends Department {
  children: DepartmentNode[];
}

export interface Team {
  id: string;
  name: string;
  slug: string;
  type: 'functional' | 'project' | 'communication';
  purpose: string | null;
  department_id: string | null;
  project_id: string | null;
  member_count: number;
  archived_at: string | null;
}

export interface TeamMember {
  employee_id: string;
  name: string;
  slug: string;
  job_title: string;
  role_in_team: 'lead' | 'member';
  joined_at: string;
}

export interface Employee {
  id: string;
  name: string;
  slug: string;
  job_title: string;
  seniority: 'junior' | 'mid' | 'senior' | 'lead' | 'principal' | 'executive';
  status: 'pending_approval' | 'active' | 'paused' | 'suspended' | 'terminated';
  /** Sin versión publicada no se le puede abrir sesión: una tarea suya se bloquea al arrancar. */
  has_published_profile: boolean;
  avatar_url: string | null;
  department_id: string | null;
  primary_team_id: string | null;
  reports_to_employee_id: string | null;
  hierarchy_level: number;
  created_at: string;
}

export interface EmployeeDetail extends Employee {
  profile: {
    is_dirty: boolean;
    last_published_at: string | null;
    current_version: number | null;
    current_version_id: string | null;
    compiled_hash: string | null;
  };
  capabilities: unknown[];
  stats: Record<string, number>;
}

/**
 * `GET /companies/{id}/org-chart` (`12 §4`).
 *
 * The field names are the chart's own and not the employee's: `level` rather
 * than `hierarchy_level`, `reports_to` rather than `reports_to_employee_id`.
 * They differ because this endpoint answers a different question — it is a
 * graph, not a list of employees — and getting them wrong here is invisible
 * until a node renders "level undefined".
 */
export interface OrgChartNode {
  id: string;
  name: string;
  slug: string;
  job_title: string;
  seniority: Employee['seniority'];
  status: Employee['status'];
  avatar_url: string | null;
  level: number;
  reports_to: string | null;
  department_id: string | null;
  team_id: string | null;
}

export interface OrgChart {
  nodes: OrgChartNode[];
  /** Already computed by the server; the client never derives them from `reports_to`. */
  edges: { id: string; source: string; target: string }[];
  levels: { level: number; count: number }[];
  warnings: { code: string; message: string; employee_ids: string[] }[];
}

/**
 * One row of the configuration dry run. The `key` is the contract
 * (`project.{slug}.lead`); the `detail` arrives already translated and is for
 * people, never for branching.
 */
export interface OrgReadinessCheck {
  key: string;
  /**
   * `not_applicable` solo lo emite el informe de puesta a punto —una
   * comprobación sin nada que mirar—, y está aquí porque el ayudante de
   * reparaciones junta los dos informes en una lista y los pinta con el mismo
   * componente. Nunca cuenta para el estado general.
   */
  status: 'ok' | 'warning' | 'error' | 'not_applicable';
  detail: string;
}

export interface OrgReadinessReport {
  status: OrgReadinessCheck['status'];
  checks: OrgReadinessCheck[];
}

/** The four blocks, exactly as `PUT /profile` expects them back. */
export interface ProfileDraft {
  identity: Record<string, unknown>;
  capabilities: Record<string, unknown>;
  rules: Record<string, unknown>;
  context: Record<string, unknown>;
  is_dirty: boolean;
  last_published_at: string | null;
  updated_at: string;
}

export interface ProfileFinding {
  check: string;
  level: 'error' | 'warning';
  field: string;
  code: string;
  message: string;
  suggestion?: string | null;
}

export interface CompiledSection {
  key: string;
  heading: string;
  offset: number;
  length: number;
  estimated_tokens: number;
}

export interface CompiledSummary {
  compiled_hash: string;
  compiler_version: string;
  estimated_tokens: number;
  token_estimator_version: string;
  cache_breakpoint_offset: number;
  sections: CompiledSection[];
}

export interface ProfileReport {
  valid: boolean;
  errors: ProfileFinding[];
  warnings: ProfileFinding[];
  compiled?: CompiledSummary;
  /**
   * Lo que el borrador compila, cuando llegó lo bastante lejos para compilar.
   *
   * Lo mandan los endpoints del asistente, que no tienen sitio para el resumen
   * entero; `POST /profile/validate` manda `compiled` en su lugar. Nulo no es
   * un error: un borrador con errores no llega al compilador.
   */
  estimated_tokens?: number | null;
}

export interface CompiledPreview extends CompiledSummary {
  system_prompt: string;
  warnings: ProfileFinding[];
}

export interface ProfileVersion {
  id: string;
  version: number;
  compiled_hash: string;
  compiler_version: string;
  estimated_tokens: number;
  token_estimator_version: string;
  change_note: string | null;
  created_by_type: string;
  created_by_id: string;
  created_at: string;
  is_current: boolean;
}

export interface ProfileVersionDetail extends ProfileVersion {
  payload: Record<string, unknown>;
  system_prompt: string;
  cache_breakpoint_offset: number;
  sections: CompiledSection[];
}

export interface PayloadChange {
  path: string;
  op: 'add' | 'remove' | 'replace';
  before: unknown;
  after: unknown;
}

export interface PromptHunk {
  section: string | null;
  header: string;
  old_start: number;
  old_lines: number;
  new_start: number;
  new_lines: number;
  lines: { op: 'context' | 'add' | 'remove'; text: string }[];
}

export interface ProfileDiff {
  from: { version: number; compiled_hash: string; estimated_tokens: number };
  to: { version: number; compiled_hash: string; estimated_tokens: number };
  payload_changes: PayloadChange[];
  prompt_diff: { format: string; hunks: PromptHunk[] };
  section_token_deltas: { section: string; from: number; to: number; delta: number }[];
}

export interface EmployeeTemplate {
  key: string;
  label: string;
  description: string;
  job_title: string;
  seniority: Employee['seniority'];
  suggested_slug: string;
  model: string;
  reasoning_effort: string;
  capabilities: { key: string; constraints: Record<string, unknown> }[];
  estimated_tokens: number;
  token_estimator_version: string;
  sample_company: string;
}

/**
 * A template as the company picker shows it: the twelve of `04 §9` and the
 * roles this company wrote itself, told apart by `origin`.
 */
export interface CompanyEmployeeTemplate extends EmployeeTemplate {
  origin: 'platform' | 'company';
  /** Only a company's own has one — it is the row you can delete. */
  id: string | null;
}

export type AssistSubject = 'template' | 'profile' | 'org' | 'staffing' | 'readiness';

/**
 * El plan de organización que escribe el asistente cuando el sujeto es `org`
 * (`config/schemas/org-plan.v1.json`).
 *
 * Todo apunta a todo por `ref` porque nada del plan existe todavía y no tiene
 * identificador. Un `*_ref` puede ser también el **slug** de algo que la
 * empresa ya tiene: así un plan amplía una organización en lugar de describir
 * una segunda.
 */
export interface OrgPlanDepartment {
  ref: string;
  name: string;
  slug: string;
  mission?: string | null;
  color?: string | null;
  parent_ref?: string | null;
}

export interface OrgPlanTeam {
  ref: string;
  name: string;
  slug: string;
  purpose?: string | null;
  department_ref?: string | null;
}

/** Los cuatro bloques viajan dentro, con la forma que ya tiene `ProfileDraft`. */
export interface OrgPlanTemplate {
  key: string;
  label: string;
  description: string;
  job_title: string;
  seniority: Employee['seniority'];
  suggested_slug?: string;
  identity?: Record<string, unknown>;
  capabilities?: Record<string, unknown>;
  rules?: Record<string, unknown>;
  context?: Record<string, unknown>;
}

export interface OrgPlanRole {
  ref: string;
  name: string;
  template_key: string;
  job_title?: string | null;
  seniority?: Employee['seniority'] | null;
  department_ref?: string | null;
  team_ref?: string | null;
  reports_to_ref?: string | null;
}

export interface OrgPlan {
  departments: OrgPlanDepartment[];
  teams: OrgPlanTeam[];
  templates: OrgPlanTemplate[];
  roles: OrgPlanRole[];
}

/** Lo que devuelve aceptar un plan: cada cosa en la forma de su propio endpoint. */
export interface OrgPlanApplied {
  templates: CompanyEmployeeTemplate[];
  departments: Department[];
  teams: Team[];
  employees: Employee[];
}

/**
 * Lo que hace una fila del plan de reparación cuando se aplica
 * (`config/schemas/readiness-plan.v1.json`).
 *
 * Ninguna quita a nadie: el documento es una lista de propuestas, así que una
 * fila de menos es un plan más pequeño y nunca una baja silenciosa. Lo que sí
 * llega a quitar —una capacidad inservible, una política que nunca gana, un
 * repositorio inalcanzable— se pinta aparte y se dice por su nombre.
 *
 * Las seis primeras contestan al organigrama; las seis últimas, al panel de
 * empresa. Un solo documento porque describen una sola empresa.
 */
export type RemediationOp =
  | 'grant_capability'
  | 'revoke_capability'
  | 'set_member'
  | 'set_project_lead'
  | 'set_manager'
  | 'hire'
  | 'archive_repository'
  | 'sweep_reservation'
  | 'fix_policy_selector'
  | 'set_policy_priority'
  | 'disable_policy'
  | 'create_team';

/** Las que le quitan algo a alguien: el panel las separa y la confirmación las nombra. */
export const REMEDIATION_REMOVALS: RemediationOp[] = ['revoke_capability', 'disable_policy', 'archive_repository'];

/** Las cuatro que alguna fase exige; son las únicas que un plan puede escribir. */
export const REMEDIATION_CAPABILITIES = ['repo.read', 'repo.write', 'pr.review', 'pr.open'] as const;

export interface RemediationAction {
  ref: string;
  op: RemediationOp;
  reason: string;
  /** El `key` de cada comprobación que esta fila contesta. */
  fixes?: string[] | null;
  employee_slug?: string | null;
  capability_key?: string | null;
  project_slug?: string | null;
  role_in_project?: ProjectRole | null;
  allocation_pct?: number | null;
  access_level?: AccessLevel | null;
  manager_slug?: string | null;
  name?: string | null;
  template_key?: string | null;
  job_title?: string | null;
  seniority?: Employee['seniority'] | null;
  department_slug?: string | null;
  team_slug?: string | null;
  reports_to_slug?: string | null;
  capabilities?: string[] | null;
  /** Por id y no por nombre: el nombre de una política es texto editable y puede repetirse. */
  policy_id?: string | null;
  subject_selector?: string | null;
  target_selector?: string | null;
  priority?: number | null;
  /** `owner/name`, que es lo que hace único a un repositorio y lo que se lee en GitHub. */
  repository_full_name?: string | null;
  team_name?: string | null;
  team_type?: 'functional' | 'project' | 'communication' | null;
}

export interface RemediationPlan {
  actions: RemediationAction[];
}

/**
 * Lo que devuelve aplicarlo: los dos informes, antes y después.
 *
 * La respuesta a «¿ha servido?» es el panel que la persona estaba mirando,
 * vuelto a preguntar. Un recuento de escrituras contestaría a otra cosa.
 */
export interface ReadinessPlanApplied {
  company_id: string;
  before: OrgReadinessReport;
  after: OrgReadinessReport;
  counts: Record<string, number>;
  checks_cleared: number;
  hired: Employee[];
  members: ProjectMember[];
  granted: { employee: string; capability: string }[];
  revoked: { employee: string; capability: string }[];
}

export interface AssistConversation {
  id: string;
  subject: AssistSubject;
  employee_id: string | null;
  /** El proyecto que se está dotando, cuando el sujeto es `staffing`. */
  project_id: string | null;
  status: 'drafting' | 'accepted' | 'discarded';
  /**
   * Los cuatro bloques de una ficha, el plan de organización cuando el sujeto
   * es `org`, o el de personal cuando es `staffing`. Sin estrechar por sujeto:
   * es una columna JSONB cuyo significado lo decide `subject`, y modelarlo aquí
   * sería modelar el servidor.
   */
  candidate: Record<string, unknown>;
  meta: Record<string, unknown>;
  cost_micro_usd: number;
  transcript: { role: 'user' | 'assistant'; text: string }[];
  created_at: string;
  updated_at: string;
  /**
   * Present when the conversation was opened with a first message. El SPA ya
   * no abre así —el primer turno lo manda el panel, con el stream ya abierto—
   * pero el contrato lo sigue admitiendo.
   */
  turn?: AssistTurn;
}

export interface AssistTurn {
  /**
   * Lo que distingue las dos copias de un turno: el POST contesta con él y el
   * stream lo repite. Quien oye los dos absorbe una sola vez.
   */
  turn_id: string;
  reply: string;
  /** Los bloques de la ficha, o las secciones del plan, que reescribió el turno. */
  blocks: string[];
  candidate: Record<string, unknown>;
  meta: Record<string, unknown>;
  cost_micro_usd: number;
  /** Null when the turn asked a question instead of writing. */
  report: ProfileReport | null;
}

/** Lo que contesta una edición a mano: un turno menos lo que solo tiene un turno. */
export interface AssistDraft {
  candidate: Record<string, unknown>;
  meta: Record<string, unknown>;
  cost_micro_usd: number;
  report: ProfileReport;
}

export interface CapabilityDefinition {
  key: string;
  group: string;
  description: string;
  spends: boolean;
  constraints: { key: string; kind: string; description: string; values: string[] }[];
}

export interface ModelOption {
  reasoning_efforts?: string[];
  supports_effort?: boolean;
  supports_adaptive_thinking?: boolean;
  max_output_tokens?: number | null;
  provider: string;
  model: string;
  display_name: string;
  context_window: number;
  input_micro_usd_per_mtok: number;
  output_micro_usd_per_mtok: number;
}

export const departments = {
  list: (companyId: string) => authFetch<{ data: DepartmentNode[] }>(`/companies/${companyId}/departments`),
  create: (companyId: string, body: Record<string, unknown>) =>
    authFetch<Department>(`/companies/${companyId}/departments`, { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>) =>
    authFetch<Department>(`/departments/${id}`, { method: 'PATCH', body }),
  archive: (id: string) => authFetch<Department>(`/departments/${id}`, { method: 'DELETE' }),
};

export const teams = {
  list: (companyId: string) => authFetch<Page<Team>>(`/companies/${companyId}/teams`),
  create: (companyId: string, body: Record<string, unknown>) =>
    authFetch<Team>(`/companies/${companyId}/teams`, { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>) => authFetch<Team>(`/teams/${id}`, { method: 'PATCH', body }),
  archive: (id: string) => authFetch<Team>(`/teams/${id}`, { method: 'DELETE' }),
  members: (id: string) => authFetch<{ data: TeamMember[] }>(`/teams/${id}/members`),
  addMember: (id: string, body: { employee_id: string; role_in_team?: string }) =>
    authFetch<{ data: TeamMember[] }>(`/teams/${id}/members`, { method: 'POST', body }),
  removeMember: (id: string, employeeId: string) =>
    authFetch<{ data: TeamMember[] }>(`/teams/${id}/members/${employeeId}`, { method: 'DELETE' }),
};

export const orgChart = {
  get: (companyId: string) => authFetch<OrgChart>(`/companies/${companyId}/org-chart`),
  /**
   * Answers with the **whole chart**, not with the moved employee. Reassigning
   * recalculates the level of every descendant, so a client that patched one
   * node would show stale levels on each of them — the exact failure the cascade
   * exists to prevent.
   */
  reassign: (companyId: string, body: { employee_id: string; new_manager_id: string | null }) =>
    authFetch<OrgChart>(`/companies/${companyId}/org-chart/reassign`, { method: 'POST', body }),
  /**
   * Could a task travel `queued` → `done` with the organization as drawn?
   * Same rules the orchestrator applies at dispatch time, asked while somebody
   * is looking at the chart instead of when a task is already stuck on them.
   */
  readiness: (companyId: string) => authFetch<OrgReadinessReport>(`/companies/${companyId}/org-chart/readiness`),
};

export const employees = {
  list: (companyId: string, params = new URLSearchParams()) =>
    authFetch<Page<Employee>>(`/companies/${companyId}/employees?${params.toString()}`),
  create: (companyId: string, body: Record<string, unknown>) =>
    authFetch<EmployeeDetail>(`/companies/${companyId}/employees`, { method: 'POST', body }),
  fromTemplate: (companyId: string, body: Record<string, unknown>) =>
    authFetch<EmployeeDetail>(`/companies/${companyId}/employees/from-template`, { method: 'POST', body }),
  get: (id: string) => authFetch<EmployeeDetail>(`/employees/${id}`),
  update: (id: string, body: Record<string, unknown>) =>
    authFetch<EmployeeDetail>(`/employees/${id}`, { method: 'PATCH', body }),
  pause: (id: string) => authFetch<EmployeeDetail>(`/employees/${id}/pause`, { method: 'POST', body: {} }),
  resume: (id: string) => authFetch<EmployeeDetail>(`/employees/${id}/resume`, { method: 'POST', body: {} }),
  terminate: (id: string, body: Record<string, unknown>) =>
    authFetch<EmployeeDetail>(`/employees/${id}/terminate`, { method: 'POST', body }),
};

export const profile = {
  get: (employeeId: string) => authFetch<ProfileDraft>(`/employees/${employeeId}/profile`),
  replace: (employeeId: string, body: Record<string, unknown>) =>
    authFetch<ProfileDraft>(`/employees/${employeeId}/profile`, { method: 'PUT', body }),
  patch: (employeeId: string, body: Record<string, unknown>) =>
    authFetch<ProfileDraft>(`/employees/${employeeId}/profile`, { method: 'PATCH', body }),
  /** Always 200, including for a draft that cannot be published (D2-15). */
  validate: (employeeId: string, body: Record<string, unknown> = {}) =>
    authFetch<ProfileReport>(`/employees/${employeeId}/profile/validate`, { method: 'POST', body }),
  preview: (employeeId: string, body: Record<string, unknown> = {}) =>
    authFetch<CompiledPreview>(`/employees/${employeeId}/profile/preview`, { method: 'POST', body }),
  publish: (employeeId: string, body: { change_note?: string } = {}) =>
    authFetch<ProfileVersionDetail>(`/employees/${employeeId}/profile/publish`, { method: 'POST', body }),
  versions: (employeeId: string) => authFetch<Page<ProfileVersion>>(`/employees/${employeeId}/profile/versions`),
  version: (employeeId: string, version: number) =>
    authFetch<ProfileVersionDetail>(`/employees/${employeeId}/profile/versions/${version}`),
  diff: (employeeId: string, a: number, b: number) =>
    authFetch<ProfileDiff>(`/employees/${employeeId}/profile/versions/${a}/diff/${b}`),
  revert: (employeeId: string, version: number, body: { change_note?: string } = {}) =>
    authFetch<ProfileVersionDetail>(`/employees/${employeeId}/profile/versions/${version}/revert`, {
      method: 'POST',
      body,
    }),
};

export const employeeTemplates = {
  list: (companyId: string) =>
    authFetch<{ data: CompanyEmployeeTemplate[] }>(`/companies/${companyId}/employee-templates`),
  remove: (id: string) => authFetch<null>(`/employee-templates/${id}`, { method: 'DELETE' }),
};

/**
 * Lo que devuelve aceptar un plan de personal.
 *
 * `removed` es la única cuenta que el navegador va a tener de quién sale: esas
 * filas ya no existen en la base de datos cuando esto se escribe, así que
 * viajan tal y como eran el instante anterior. La pantalla las enseña por su
 * nombre.
 */
export interface StaffingPlanApplied {
  project_id: string;
  project_slug: string;
  hired: Employee[];
  added: ProjectMember[];
  updated: ProjectMember[];
  removed: Record<string, unknown>[];
  access: { employee_id: string; employee_slug: string; access_level: string; repository_ids: string[] | null }[];
}

export const assist = {
  open: (companyId: string, body: Record<string, unknown>) =>
    authFetch<AssistConversation>(`/companies/${companyId}/assist/conversations`, { method: 'POST', body }),
  get: (id: string) => authFetch<AssistConversation>(`/assist/conversations/${id}`),
  turn: (id: string, message: string) =>
    authFetch<AssistTurn>(`/assist/conversations/${id}/turns`, { method: 'POST', body: { message } }),
  /**
   * Lo que la persona cambia a mano en el formulario, guardado y **vuelto a
   * validar**. La respuesta trae el informe recién calculado porque es el mismo
   * validador que juzgará `accept`: sin esta ida y vuelta el formulario y el
   * botón de guardar opinan cosas distintas sobre el mismo borrador.
   *
   * No es un turno y no gasta: no llama al modelo.
   */
  edit: (id: string, body: { candidate?: Record<string, unknown>; meta?: Record<string, unknown> }) =>
    authFetch<AssistDraft>(`/assist/conversations/${id}/candidate`, { method: 'PATCH', body }),
  accept: (id: string, body: { key?: string } = {}) =>
    authFetch<CompanyEmployeeTemplate>(`/assist/conversations/${id}/accept`, { method: 'POST', body }),
  /**
   * El mismo `accept`, tipado para un plan: crea los departamentos, los equipos
   * y las contrataciones en una sola transacción y devuelve todo lo creado.
   *
   * Dos funciones y no una unión porque quien las llama nunca duda de cuál
   * quiere: la pantalla del plan no acepta plantillas y la del creador no
   * aplica planes.
   */
  apply: (id: string) => authFetch<OrgPlanApplied>(`/assist/conversations/${id}/accept`, { method: 'POST', body: {} }),
  /**
   * El mismo `accept` otra vez, tipado para un plan de personal: contrata,
   * asigna, cambia y da de baja en una sola transacción.
   *
   * Tercera función sobre el mismo endpoint, por la razón que ya da `apply`:
   * quien la llama nunca duda de cuál quiere.
   */
  applyStaffing: (id: string) =>
    authFetch<StaffingPlanApplied>(`/assist/conversations/${id}/accept`, { method: 'POST', body: {} }),
  /**
   * El mismo `accept` una vez más, tipado para un plan de reparación: concede,
   * revoca, asigna, nombra y contrata en una transacción, y devuelve el
   * verificador antes y después.
   */
  applyReadiness: (id: string) =>
    authFetch<ReadinessPlanApplied>(`/assist/conversations/${id}/accept`, { method: 'POST', body: {} }),
  discard: (id: string) => authFetch<null>(`/assist/conversations/${id}`, { method: 'DELETE' }),
};

export const catalog = {
  employeeTemplates: () => authFetch<{ data: EmployeeTemplate[] }>('/catalog/employee-templates'),
  capabilities: () =>
    authFetch<{ data: CapabilityDefinition[]; groups: { key: string; label: string }[] }>('/catalog/capabilities'),
  models: () => authFetch<{ data: ModelOption[]; reasoning_efforts: string[] }>('/catalog/models'),
};
